import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowLeft, Sparkles, Ticket } from 'lucide-react';

const DEADLINE_KEY = 'mh_first_purchase_banner_deadline_v1';

/** مهلت شمارش معکوس: یک‌بار برای هر مرورگر ذخیره می‌شود (پیش‌فرض ۷۲ ساعت از اولین بازدید) */
function getOrSetDeadlineMs(): number {
  try {
    const existing = localStorage.getItem(DEADLINE_KEY);
    if (existing) {
      const t = new Date(existing).getTime();
      if (Number.isFinite(t) && t > Date.now()) return t;
    }
  } catch {
    /* ignore */
  }
  const end = Date.now() + 72 * 3600 * 1000;
  try {
    localStorage.setItem(DEADLINE_KEY, new Date(end).toISOString());
  } catch {
    /* ignore */
  }
  return end;
}

const pad2 = (n: number) => String(Math.max(0, Math.min(99, n))).padStart(2, '0');

/**
 * بنر بالای سایت: پیشنهاد کد تخفیف اولین خرید + تایمر شمارش معکوس
 * فقط دسکتاپ (≥sm)؛ افکت‌های بیرون زده با overflow قطع می‌شوند تا اسکرول افقی نماند.
 */
const SM_MIN = '(min-width: 640px)';

const FirstPurchasePromoBanner: React.FC = () => {
  const [showDesktop, setShowDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(SM_MIN).matches
  );
  const [dismissed, setDismissed] = useState(false);
  const [endMs] = useState(() => getOrSetDeadlineMs());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia(SM_MIN);
    const sync = () => setShowDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!showDesktop || dismissed) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [showDesktop, dismissed]);

  const { h, m, s, expired } = useMemo(() => {
    const left = Math.floor((endMs - Date.now()) / 1000);
    if (left <= 0) return { h: 0, m: 0, s: 0, expired: true };
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = left % 60;
    return { h, m, s, expired: false };
  }, [endMs, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick هر ثانیه شمارش را تازه می‌کند

  const dismiss = () => setDismissed(true);

  if (!showDesktop || dismissed || expired) return null;

  const timerCells = (
    [
      { v: pad2(s), label: 'ثانیه', wide: false },
      { v: pad2(m), label: 'دقیقه', wide: false },
      { v: h >= 100 ? String(h) : pad2(Math.min(99, h)), label: 'ساعت', wide: true },
    ] as const
  ).map((x) => (
    <div key={x.label} className="flex flex-col items-center gap-0.5">
      <div
        className={`flex h-[1.875rem] min-h-[1.875rem] items-center justify-center rounded-md bg-white text-[11px] font-black leading-none text-slate-800 shadow-sm ring-1 ring-black/5 sm:h-9 sm:min-h-9 sm:text-sm ${x.wide ? 'min-w-[1.72rem] px-1 sm:min-w-[2.35rem]' : 'min-w-[1.65rem] px-1 sm:min-w-[2rem]'}`}
      >
        {x.v}
      </div>
      <span className="text-[7px] font-bold leading-none text-white/90 sm:text-[8px]">{x.label}</span>
    </div>
  ));

  const badgeCluster = (
    <div className="relative shrink-0">
      <div className="absolute -inset-0.5 rounded-lg bg-white/25 blur-[2px]" aria-hidden />
      <div className="relative flex items-center gap-1 rounded-lg bg-white/18 px-1.5 py-1 ring-1 ring-white/35 backdrop-blur-sm sm:gap-1.5 sm:px-2">
        <span className="relative flex h-[1.875rem] w-[1.875rem] shrink-0 items-center justify-center rounded-md bg-white/95 text-[#469189] shadow-sm sm:h-9 sm:w-9">
          <Ticket className="h-[0.95rem] w-[0.95rem] stroke-[2.5] sm:h-[1.1rem] sm:w-[1.1rem]" aria-hidden />
        </span>
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-200 drop-shadow-sm sm:h-4 sm:w-4" aria-hidden />
      </div>
    </div>
  );

  const textAndButtons = (
    <>
      <p className="m-0 w-full min-w-0 text-pretty text-center text-[13px] font-extrabold leading-[1.72] tracking-tight text-white sm:inline sm:w-auto sm:text-start sm:text-sm md:text-[15px] md:leading-[1.75] [&_strong]:font-black">
        <strong className="text-white drop-shadow-sm">دریافت کد تخفیف اولین خرید:</strong>{' '}
        <span className="font-bold text-white/95">ثبت‌نام یا ورود؛ تخفیف در اولین خرید دوره‌ها.</span>
      </p>
      <div className="flex w-full min-w-0 flex-wrap items-center justify-center gap-2 sm:inline-flex sm:w-auto sm:shrink-0 sm:flex-nowrap sm:justify-start sm:gap-2">
        <Link
          to="/register"
          className="inline-flex min-h-[40px] min-w-0 items-center justify-center gap-0.5 rounded-full bg-rose-500 px-3 py-2 text-[11px] font-black text-white shadow-md ring-1 ring-rose-400/80 transition hover:bg-rose-600 sm:min-h-0 sm:py-1.5 sm:text-xs md:text-[13px]"
        >
          شروع کنید
          <ArrowLeft className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4" aria-hidden />
        </Link>
        <Link
          to="/courses"
          className="inline-flex min-h-[40px] min-w-0 items-center justify-center rounded-full bg-white/15 px-3 py-2 text-[11px] font-extrabold text-white backdrop-blur-sm ring-1 ring-white/40 transition hover:bg-white/25 sm:min-h-0 sm:py-1.5 sm:text-xs md:text-[13px]"
        >
          دوره‌ها
        </Link>
      </div>
    </>
  );

  const closeBtn = (
    <button
      type="button"
      onClick={dismiss}
      className="shrink-0 rounded-lg p-1.5 text-white/90 ring-1 ring-transparent transition hover:bg-white/15 hover:text-white hover:ring-white/15 sm:p-0.5 sm:ring-0"
      aria-label="بستن بنر تبلیغاتی"
    >
      <X className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={2.25} />
    </button>
  );

  return (
    <div
      dir="rtl"
      className="relative min-w-0 w-full max-w-full overflow-hidden border-b border-white/25 bg-gradient-to-l from-[#5eaea8] via-[#73b9b4] to-[#6db0ab] text-white"
      style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top, 0px))' }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='3' cy='5' r='1' fill='%23ffffff'/%3E%3Ccircle cx='15' cy='14' r='0.8' fill='%23ffffff'/%3E%3Ccircle cx='21' cy='8' r='0.6' fill='%23ffffff'/%3E%3C/svg%3E")`,
          backgroundSize: 'clamp(40px, 12vw, 56px) clamp(40px, 12vw, 56px)',
        }}
      />
      <div className="pointer-events-none absolute -left-10 top-1/2 h-[4.5rem] w-[4.5rem] max-w-[50vw] -translate-y-1/2 rounded-full bg-white/20 blur-2xl" />
      <div className="pointer-events-none absolute -right-6 top-0 h-16 w-32 max-w-[45vw] rounded-full bg-rose-200/25 blur-2xl" />
      <div className="pointer-events-none absolute bottom-0 right-1/3 h-8 w-20 max-w-[40vw] rounded-full bg-sky-200/20 blur-xl" />

      <div className="relative mx-auto flex min-w-0 max-w-7xl items-center gap-1.5 px-[max(0.5rem,env(safe-area-inset-right,0px))] py-2 pl-[max(0.5rem,env(safe-area-inset-left,0px))] sm:gap-2.5 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center">
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1.5 md:gap-x-2.5">{textAndButtons}</div>
        </div>
        <div className="mx-0.5 h-9 w-px shrink-0 bg-white/30" aria-hidden />
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">{timerCells}</div>
        {badgeCluster}
        {closeBtn}
      </div>
    </div>
  );
};

export default FirstPurchasePromoBanner;
